const normalise = value => String(value ?? '').trim().normalize('NFC').toLowerCase();
const normaliseLanguage = value => ['ja', 'jpn', 'japanese'].includes(normalise(value)) ? 'jp' : normalise(value);

const importError = (issues, rows) => {
  const error = new Error(`${issues.length} CSV issue${issues.length === 1 ? '' : 's'} need attention. Your existing cart has been kept.`);
  error.issues = issues;
  error.rows = rows;
  return error;
};

export function parseCartCsv(text) {
  const records = []; let record = [], cell = '', quoted = false, line = 1, startLine = 1;
  const source = String(text).replace(/^\uFEFF/, '');
  for (let i = 0; i < source.length; i++) {
    const ch = source[i];
    if (ch === '"') {
      if (quoted && source[i + 1] === '"') { cell += '"'; i++; }
      else quoted = !quoted;
    } else if (!quoted && (ch === ',' || ch === '\n' || ch === '\r')) {
      record.push(cell); cell = '';
      if (ch !== ',') {
        if (record.some(value => value.trim())) records.push({ values: record, rowNumber: startLine });
        record = [];
        if (ch === '\r' && source[i + 1] === '\n') i++;
        line++; startLine = line;
      }
    } else {
      cell += ch;
      if (ch === '\n' || (ch === '\r' && source[i + 1] !== '\n')) line++;
    }
  }
  if (quoted) throw importError([{ rowNumber: startLine, name: '', message: 'CSV contains an unclosed quoted value.', candidates: [] }], []);
  record.push(cell);
  if (record.some(value => value.trim())) records.push({ values: record, rowNumber: startLine });
  const header = (records.shift()?.values || []).map(normalise);
  const issues = [];
  for (const name of ['name', 'condition', 'printing', 'language', 'quantity', 'price type']) {
    if (!header.includes(name)) issues.push({ rowNumber: 1, name: '', message: `CSV is missing the ${name} column.`, candidates: [] });
  }
  if (issues.length) throw importError(issues, []);
  const rows = records.filter(({ values }) => values[header.indexOf('name')]?.trim()).map(({ values, rowNumber }) => {
    const get = name => (values[header.indexOf(name)] || '').trim();
    const quantity = Number(get('quantity'));
    const add = message => issues.push({ rowNumber, name: get('name'), message, candidates: [] });
    if (!Number.isSafeInteger(quantity) || quantity < 1 || quantity > 1000) add('Quantity must be a whole number between 1 and 1000.');
    if (!['cash', 'credit'].includes(normalise(get('price type')))) add('Choose Cash or Credit.');
    for (const name of ['condition', 'printing', 'language']) if (!get(name)) add(`${name} is required.`);
    return { rowNumber, name: get('name'), condition: get('condition'), printing: get('printing'), language: get('language'), quantity, isCash: normalise(get('price type')) === 'cash', productId: get('product id'), skuId: get('sku id'), game: get('game'), set: get('set'), number: get('card number') };
  });
  if (!rows.length) issues.push({ rowNumber: 1, name: '', message: 'CSV contains no card rows.', candidates: [] });
  if (rows.reduce((sum, row) => sum + (Number.isSafeInteger(row.quantity) ? row.quantity : 0), 0) > 1000) issues.push({ rowNumber: 1, name: '', message: 'Import at most 1000 copies at a time.', candidates: [] });
  if (issues.length) throw importError(issues, rows);
  return rows;
}

export async function prepareCartCsv(rows, { backendUrl, username, enabledGames, allowedConditions, excludedSkus = [], calculatePrice, fetchImpl = fetch }) {
  if (!enabledGames?.length || !allowedConditions?.length) throw new Error('Please wait for the buylist settings to load.');
  if (rows.reduce((sum, row) => sum + (Number.isSafeInteger(row.quantity) ? row.quantity : 0), 0) > 1000) throw importError([{ rowNumber: 1, name: '', message: 'Import at most 1000 copies at a time.', candidates: [] }], rows);
  const cache = new Map(), staged = [], issues = [];
  const excluded = new Set(excludedSkus.map(String));
  const batchId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  const eligibleSku = (card, sku, row, honorSkuId = true) => {
    if (normalise(sku.condAbbr) !== normalise(row.condition) || normalise(sku.printingName) !== normalise(row.printing) || normaliseLanguage(sku.langAbbr) !== normaliseLanguage(row.language)) return false;
    if (honorSkuId && row.skuId && String(sku.skuId) !== String(row.skuId)) return false;
    if (excluded.has(String(sku.skuId))) return false;
    const enSku = normaliseLanguage(sku.langAbbr) !== 'en' && (card.skus || []).find(value => normalise(value.condAbbr) === normalise(sku.condAbbr) && normalise(value.printingName) === normalise(sku.printingName) && normaliseLanguage(value.langAbbr) === 'en');
    return !enSku || !excluded.has(String(enSku.skuId));
  };
  const suggestionsFor = (cards, row, preferredSet, referenceCard) => {
    const family = name => normalise(name).replace(/(?:\s*\((?:rainbow\s+)?foil\))+$/, '').trim();
    const referenceName = family(referenceCard?.name || row.name);
    const referenceNumber = normalise(referenceCard?.number || row.number);
    const rank = card => (referenceName && family(card.name) === referenceName ? 4 : 0)
      + (referenceNumber && normalise(card.number) === referenceNumber ? 2 : 0)
      + (preferredSet && normalise(card.expansionName) === normalise(preferredSet) ? 1 : 0);
    const eligible = cards.filter(card => (card.skus || []).some(sku => eligibleSku(card, sku, row, false)));
    eligible.sort((left, right) => rank(right) - rank(left));
    return eligible.slice(0, 20).map(card => ({ productId: card.productId, name: card.name, set: card.expansionName || '', number: card.number || '' }));
  };
  const lookup = async (row, useId) => {
    const key = JSON.stringify([useId ? 'id' : 'name', useId ? String(row.productId) : normalise(row.name), normalise(row.game)]);
    if (cache.has(key)) return cache.get(key);
    const found = new Map();
    for (const game of row.game ? [row.game] : enabledGames) {
      let pages = 1;
      for (let page = 1; page <= pages; page++) {
        const url = new URL(`${backendUrl}/catalog`);
        url.search = new URLSearchParams({ username, game, search: useId ? '' : row.name, ...(useId ? { productId: row.productId } : {}), page: String(page), limit: '100', showNotBuying: 'false', showBulk: 'true' }).toString();
        const response = await fetchImpl(url.toString());
        if (!response.ok) throw new Error('Catalog lookup failed. Please try again.');
        const data = await response.json();
        if (!Array.isArray(data.items)) throw new Error('Invalid catalog response.');
        pages = Math.max(1, Number(data.totalPages) || 1);
        if (pages > 100) throw new Error('Too many matches; narrow the game or provide a Product ID.');
        for (const card of data.items) {
          if (useId && String(card.productId) !== String(row.productId)) continue;
          if (!enabledGames.some(value => normalise(value) === normalise(card.gameName))) continue;
          if (row.game && normalise(card.gameName) !== normalise(row.game)) continue;
          found.set(String(card.productId), card);
        }
      }
    }
    const result = [...found.values()];
    cache.set(key, result);
    return result;
  };
  for (const [index, row] of rows.entries()) {
    let suggestions = [];
    try {
      if (!Number.isSafeInteger(row.quantity) || row.quantity < 1 || row.quantity > 1000) throw new Error('Quantity must be a whole number between 1 and 1000.');
      if (!allowedConditions.some(value => normalise(value) === normalise(row.condition))) throw new Error('This condition is not accepted.');
      if (row.game && !enabledGames.some(value => normalise(value) === normalise(row.game))) throw new Error('This game is not accepted.');
      const byId = Boolean(String(row.productId ?? '').trim());
      let cards = await lookup(row, byId);
      if (!cards.length && byId) {
        cards = await lookup(row, false);
        suggestions = suggestionsFor(cards, row, row.set);
        throw new Error('Product ID was not found in an accepted game. Choose the correct card or correct the CSV.');
      }
      suggestions = suggestionsFor(cards, row, row.set);
      const candidates = byId ? cards : cards.filter(card => normalise(card.name) === normalise(row.name) && (!row.set || normalise(card.expansionName) === normalise(row.set)) && (!row.number || normalise(card.number) === normalise(row.number)));
      const matches = candidates.flatMap(card => (card.skus || []).filter(sku => eligibleSku(card, sku, row)).map(sku => ({ card, sku })));
      if (matches.length !== 1) {
        if (!matches.length && byId) suggestions = suggestionsFor(await lookup(row, false), row, cards[0]?.expansionName || row.set, cards[0]);
        throw new Error(matches.length ? 'More than one card matches. Choose the correct card.' : 'No eligible card matches the condition, printing, language and SKU ID.');
      }
      const { card, sku } = matches[0];
      const condition = sku.condAbbr, printing = sku.printingName, language = sku.langAbbr;
      const enSku = normalise(language) !== 'en' && card.skus.find(value => normalise(value.condAbbr) === normalise(condition) && normalise(value.printingName) === normalise(printing) && normalise(value.langAbbr) === 'en');
      if (enSku && excluded.has(String(enSku.skuId))) throw new Error('This card is excluded.');
      const rowItems = [];
      for (let unit = 0; unit < row.quantity; unit++) {
        const offset = staged.filter(item => String(item.productId) === String(card.productId) && item.printing === printing && item.language === language).length + rowItems.length + 1;
        const price = await calculatePrice(card, condition, printing, language, true, false, offset);
        const offeredPrice = Number(row.isCash ? price?.cashPrice : price?.creditPrice);
        if (!Number.isFinite(offeredPrice) || offeredPrice <= 0 || price?.contactForPrice || (row.isCash ? price?.cashAvailable : price?.creditAvailable) === false) throw new Error('A current buylist price is unavailable.');
        rowItems.push({ ...card, ...price, originalBasePrice: price.basePrice, condition, printing, language, quantity: 1, skuId: enSku?.skuId || sku.skuId, isCash: row.isCash, cartItemId: `imported-${batchId}-${staged.length + rowItems.length}`, orderNote: normalise(language) !== 'en' ? `This ${card.name} is actually a ${language} language card.` : '', nonEnglish: normalise(language) !== 'en' ? { language, offeredPrice, isDerivedPrice: price.isDerivedPrice, derivedPercentage: price.derivedPercentage, basePrice: price.basePrice } : null });
      }
      staged.push(...rowItems);
    } catch (error) {
      issues.push({ rowNumber: row.rowNumber || index + 2, name: row.name, message: error.message || 'Could not import this row.', candidates: suggestions });
    }
  }
  if (issues.length) throw importError(issues, rows);
  return staged;
}

export function buildCartCsv(items, total) {
  const header = ['Name', 'Condition', 'Printing', 'Language', 'Quantity', 'Price Type', 'Price', 'Total', 'Product ID', 'SKU ID', 'Game', 'Set', 'Card Number'];
  const rows = items.map(item => {
    const price = item.isCash ? item.cashPrice : item.creditPrice;
    const quantity = item.quantity || 1;
    const sku = (item.skus || []).find(value => value.condAbbr === item.condition && value.printingName === item.printing && value.langAbbr === (item.language || 'EN'));
    return [item.name, item.condition, item.printing, item.language || 'EN', quantity, item.isCash ? 'Cash' : 'Credit', price, Number(price) * quantity, item.productId || '', sku?.skuId || item.skuId || '', item.gameName || '', item.expansionName || '', item.number || ''];
  });
  rows.push(['', '', '', '', '', '', 'Total:', total]);
  return [header.join(','), ...rows.map(row => header.map((_, index) => `"${String(row[index] ?? '').replace(/"/g, '""')}"`).join(','))].join('\r\n');
}
